import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { CustomFieldResponse } from "../components/InputCst/MultiSelectCst/types";
import { GetPayloadPropsExternalTraining, PayloadPropExternalsTraining } from "../routes/Profile/ExternalTrainingAddSkillbook/AddSkillBook.types";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

interface UseOptionsQueryParams {
  enabled?: boolean;
  queryKey?: string[];
  onSuccess?: (data: { output: [] }) => void;
  onError?: (error: string) => void;
  onSettled?: (data: { output: [] }, error: string) => void;
  knowledgeAreaId?: string | number;
}
interface UseExternalRequestQueryParams {
  enabled?: boolean;
  onSuccess?: (data: { content: GetPayloadPropsExternalTraining[] }) => void;
  onError?: (error: string) => void;
  queryParams: {
    page?: number;
    size?: number;
    learnerId: string | number;
    corporateId: string | number;
    name?: string;
    surname?: string;
    title?: string;
    durationMinutes?: number;
    startDate?: string;
    endDate?: string;
    courseType?: number;
    certificateStartValidityDate?: string;
    certificateEndValidityDate?: string;
    code?: string;
    knowledgeAreaId?: number;
    knowledgeDomainId?: number;
    capabilityId?: number;
    year?: number;
    status?: string;
    // TODO: scommentare quando il backend supporterà i filtri
    // statusFilter?: CourseStatusFilter;
    // typeFilter?: CourseTypeFilter;
    // sortBy?: string; // ordinamento per colonna (status, type, startDate)
    // sortDirection?: "asc" | "desc";
  };
  queryKey: string[];
}

export function useKnowledgeAreasOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useKnowledgeDomainsOptionsQuery({
  knowledgeAreaId,
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas/${knowledgeAreaId}/domains`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useCapabilitiesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/capabilities`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useCourseTypesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();

  const uri = "/certificates/external/course-type";

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        console.log("error", error);
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useExternalTrainingQuery({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: ["external-training-requests", "pending",...queryKey],

    queryFn: () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { content: GetPayloadPropsExternalTraining[] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useExternalTrainingQueryApproved({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: ["external-training-requests", "approved", ...queryKey],

    queryFn: () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { content: GetPayloadPropsExternalTraining[] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useExternalTrainingQueryRejected({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: ["external-training-requests", "rejected", ...queryKey],

    queryFn: () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { content: GetPayloadPropsExternalTraining[] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}


export function usePostExternalTraining({
  onSuccess,
  onError
}: UseOptionsQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const uri = "/external-trainings";

  return useMutation({
    mutationFn: async (data: PayloadPropExternalsTraining ) => {
      console.log("Invio dati a:", `${ basePath }${ uri }`);
      console.log("Dati inviati:", data);

      //const payload = {
      //  ...data,
      //  trainingMode: data.trainingMode === "" ? null : data.trainingMode
      //};

      const formData = new FormData();

      (data.attachments ?? []).forEach((file: string) => formData.append("attachments", file));
      formData.append("data", JSON.stringify(data.data));

      console.log("formData", formData);

      try {
        const response = await fetcher(
          `${ basePath }${ uri }`,
          {
            method: "POST",
            headers: {
              "authorization": idToken,
              "x-ada-session-token": sessionToken
            },
            body: formData
          }
        );
        return response;
      } catch (error) {
        console.error("Errore durante l'invio:", error);
        throw error;
      }
    },

    onSuccess: (data) => {
      console.log("Successo dalla mutazione:", data);
      if (onSuccess) onSuccess(data as unknown as  { output: [] });
    },

    onError: (error) => {
      console.error("Errore dalla mutazione:", error);
      if (onError) onError(error as unknown as string);
    }
  });
}

interface UseCustomFieldsQueryParams {
  enabled?: boolean;
  onSuccess?: (data: CustomFieldResponse[]) => void;  // ← array diretto, non { output: [] }
  onError?: (error: string) => void;
}

export function useCustomFieldsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseCustomFieldsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/external/custom-fields/${corporateInfo!.id}`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as CustomFieldResponse[]);
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}
